import CustomerAutocomplete from "./CustomerAutocomplete";
import React, { useEffect, useRef } from "react";
import "./AppointmentDialogControls.css";
import "./PhoneField.css";
import Modal from "react-modal";
import moment from "moment";
import "./RetouchAppointment.css";
import "./RetouchCalendar.css";
import "./NewAppointmentMobile.css";
import "./AppointmentFields.css";
import { FiClipboard } from "react-icons/fi";

// =======================================================
// AddAppointmentModal
// -------------------------------------------------------
// Displays the form used to create a new appointment.
//
// Responsibilities:
// - Render add-appointment form fields
// - Paste copied appointment data
// - Open reference images
// - Submit the add form
// =======================================================

const syncRetouchViewport = node => {
  if (!node) return;
  const viewport = window.visualViewport;
  node.style.setProperty('--retouch-viewport-height', `${viewport?.height || window.innerHeight}px`);
  node.style.setProperty('--retouch-viewport-top', `${viewport?.offsetTop || 0}px`);
};

const AddAppointmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  appointmentData,
  setAppointmentData,
  copiedAppointment,
  setCopiedAppointment,
  categories,
  onOpenImages,
  locationId,
  availableDays = [],
}) => {
  const retouchOverlayRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return;
    const sync = () => syncRetouchViewport(retouchOverlayRef.current);
    sync();
    window.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('scroll', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('scroll', sync);
    };
  }, [isOpen, appointmentData.calendarRetouch]);
  const isRetouch = appointmentData.appointmentKind === "retouch";
  const artistChoices = [...new Map(availableDays.map(day => [Number(day.artistId), { id: day.artistId, name: day.artistName }])).values()]
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
  const targetDays = availableDays
    .filter((day) =>
      Number(day.artistId) === Number(appointmentData.artistId) &&
      moment(day.day).isSameOrAfter(moment(), "day")
    )
    .sort((a, b) => moment(a.day).valueOf() - moment(b.day).valueOf());

  if (appointmentData.calendarRetouch) {
    return (
      <Modal isOpen={isOpen} onRequestClose={onClose} shouldCloseOnOverlayClick={false} shouldCloseOnEsc={true}
        contentLabel="Nachstechtermin planen" className="reschedule-modal retouch-plan-modal" overlayClassName="appointment-overlay retouch-plan-overlay"
        overlayRef={node => { retouchOverlayRef.current = node; syncRetouchViewport(node); }}>
        <button type="button" className="appointment-dialog-close" onClick={onClose} aria-label="Terminfenster schließen">✕</button>
        <form onSubmit={onSubmit} className="reschedule-form">
          <div className="retouch-plan-scroll">
          <div className="reschedule-header">
            <div className="reschedule-title">Nachstechen planen</div>
            <div className="reschedule-date">{moment(appointmentData.day).format('dddd')}<br /><span>{moment(appointmentData.day).format('DD. MMMM YYYY')}</span></div>
          </div>
          <div className="reschedule-card">
            <div className="reschedule-label">Kunde</div>
            <div className="reschedule-client">{appointmentData.clientName}</div>
            {appointmentData.phone && <div className="retouch-plan-phone">{appointmentData.phone}</div>}
            <details className="retouch-plan-details"><summary>Motiv anzeigen / bearbeiten</summary>
            <label className="retouch-plan-motif">Motiv
              <textarea aria-label="Motiv für Nachstechen" value={appointmentData.description || ''}
                onChange={event => setAppointmentData(previous => ({ ...previous, description: event.target.value }))} />
            </label>
            </details>
          </div>
          <div className="reschedule-arrow">↓</div>
          <div className="reschedule-card reschedule-target">
            <div className="reschedule-label">Neuer Nachstechtermin</div>
            <div className="reschedule-target-row"><span>Künstler</span><strong>{appointmentData.artistName}</strong></div>

            <label className="reschedule-target-row"><span>Uhrzeit</span>
              <input type="time" aria-label="Uhrzeit" value={appointmentData.time ? moment(appointmentData.time, 'HH:mm:ss').format('HH:mm') : ''}
                onChange={event => setAppointmentData(previous => ({ ...previous, time: event.target.value }))} required />
            </label>
            <div className="reschedule-target-row retouch-plan-duration"><span>Dauer</span>
              <div className="retouch-duration-switch" role="group" aria-label="Dauer">
                {[30, 60].map(minutes => <button key={minutes} type="button" aria-pressed={Number(appointmentData.duration) === minutes}
                  onClick={() => setAppointmentData(previous => ({ ...previous, duration: minutes }))}>{minutes} Min.</button>)}
              </div>
            </div>
            <label className="reschedule-target-row"><span>Minuten</span>
              <input type="number" aria-label="Dauer (Minuten)" min="1" value={appointmentData.duration}
                onChange={event => setAppointmentData(previous => ({ ...previous, duration: event.target.value }))} required />
            </label>
            <div className="retouch-plan-costs">Minimum 0 € · Maximum 0 € · Anzahlung 0 €</div>
          </div>
          </div>
          <div className="reschedule-actions">
            <button type="button" className="reschedule-cancel" onClick={onClose} disabled={appointmentData.isSaving}>Anderen Tag wählen</button>
            <button type="submit" className="reschedule-confirm" disabled={!appointmentData.dayId || appointmentData.isSaving}>
              {appointmentData.isSaving ? 'Wird gespeichert…' : 'Nachstechtermin speichern'}
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}
      contentLabel="Add Appointment"
      className={`appointment-modal-transparent new-appointment-modal ${
        isOpen ? "modal-zoomed" : ""
      }`}
      overlayClassName="appointment-overlay new-appointment-overlay"
      overlayRef={node => { retouchOverlayRef.current = node; syncRetouchViewport(node); }}
    >
      {isOpen && copiedAppointment && !isRetouch && (
        <button
          type="button"
          className="paste-appointment-button"
          onClick={() => {
            setAppointmentData((previousData) => ({
              ...previousData,
              clientName: copiedAppointment.clientName || "",
              phone: copiedAppointment.phone || "",
              description: copiedAppointment.description || "",
              minRange: copiedAppointment.minRange ?? "",
              maxRange: copiedAppointment.maxRange ?? "",
              deposit: copiedAppointment.deposit ?? "",
              duration: copiedAppointment.duration || "",
              tattoo_category:
                copiedAppointment.tattoo_category || "",
            }));

            setCopiedAppointment(null);
          }}
          title="Kopierten Termin einfügen"
          style={{
            position: "absolute",
            top: "20px",
            right: "76px",
            zIndex: 1001,
          }}
        >
          <FiClipboard />
        </button>
      )}

      <button type="button" className="appointment-dialog-close" onClick={onClose} aria-label="Terminfenster schließen">✕</button>

      <form
        onSubmit={onSubmit}
        className="appointment-form new-appointment-form"
      >
        <div className="new-appointment-scroll">
        {!appointmentData.calendarRetouch && <div className="appointment-kind-switch" role="group" aria-label="Terminart">
          {[['tattoo', 'Tattoo'], ['retouch', 'Nachstechen']].map(([kind, label]) => (
            <button
              key={kind}
              type="button"
              aria-pressed={(appointmentData.appointmentKind || 'tattoo') === kind}
              onClick={() => setAppointmentData((previousData) => ({
                ...previousData,
                appointmentKind: kind,
                ...(kind === 'retouch'
                  ? {
                      minRange: 0, maxRange: 0, deposit: 0, duration: 60,
                      description: /^Nachstechen\b/i.test(previousData.description || '')
                        ? previousData.description
                        : `Nachstechen${previousData.description ? ` – ${previousData.description}` : ''}`,
                    }
                  : {
                      minRange: '', maxRange: '', deposit: '', duration: '',
                      description: (previousData.description || '').replace(/^Nachstechen\b\s*(?:[–-]\s*)?/i, ''),
                    }),
              }))}
              disabled={(appointmentData.appointmentKind || 'tattoo') === kind}
            >{(appointmentData.appointmentKind || 'tattoo') === kind && <span className="appointment-kind-check" aria-hidden="true">✓</span>}{label}</button>
          ))}
        </div>}
        {appointmentData.calendarRetouch && <div className="retouch-calendar-selected"><strong>{appointmentData.artistName}</strong><button type="button" onClick={onClose}>Anderen Tag wählen</button></div>}

        {(appointmentData.chooseDay || isRetouch) && !appointmentData.calendarRetouch && (
          <div>
          <label className="retouch-day-select">
            Künstler
            <select aria-label="Künstler für Nachstechtermin" value={appointmentData.artistId ?? ''}
              onChange={(event) => setAppointmentData(previous => ({ ...previous, artistId: Number(event.target.value) || null, dayId: null, day: null, time: '', chooseDay: true }))} required>
              <option value="">Künstler auswählen</option>
              {artistChoices.map(artist => <option key={artist.id} value={artist.id}>{artist.name}</option>)}
            </select>
          </label>
          <label className="retouch-day-select">
            Termin beim ausgewählten Künstler
            <select
              aria-label="Tag für Nachstechtermin"
              value={appointmentData.dayId ?? ''}
              onChange={(event) => {
                const selectedDay = targetDays.find((day) => String(day.dayId) === event.target.value);
                setAppointmentData((previousData) => ({
                  ...previousData,
                  dayId: selectedDay?.dayId ?? null,
                  day: selectedDay?.day ?? null,
                  time: '',
                }));
              }}
              required
            >
              <option value="">Tag auswählen</option>
              {targetDays.map((day) => (
                <option key={day.dayId} value={day.dayId}>
                  {moment(day.day).format('dd, DD.MM.YYYY')} · {day.artistName} · {(day.appointments || []).length} Termine
                </option>
              ))}
            </select>
            {targetDays.length === 0 && (
              <p>Noch keine kommenden Tage für diesen Künstler hinterlegt. Bitte zuerst einen Künstler-Tag anlegen.</p>
            )}
          </label>
          </div>
        )}

        {appointmentData.day && (
          <div className="modal-day-header">
            {moment(appointmentData.day).format("dddd")} |{" "}
            {moment(appointmentData.day).format(
              "DD. MMMM YYYY"
            )}
          </div>
        )}

        <div className="appointment-form-grid">
          <div className="form-section">
            <CustomerAutocomplete
              data={appointmentData}
              setData={setAppointmentData}
              locationId={locationId}
              isOpen={isOpen}
            />
          </div>

          <div className="form-section">
            <h4>Termin</h4>

            <label className="appointment-field"><span>Uhrzeit</span>
<input
              type="time"
              value={
                appointmentData.time
                  ? moment(
                      appointmentData.time,
                      "HH:mm:ss"
                    ).format("HH:mm")
                  : ""
              }
              onChange={(event) =>
                setAppointmentData({
                  ...appointmentData,
                  time: event.target.value,
                })
              }
              required
            />
</label>

            <label className="appointment-field"><span>Motiv &amp; Körperstelle</span>
<textarea
              value={appointmentData.description}
              onChange={(event) =>
                setAppointmentData({
                  ...appointmentData,
                  description: event.target.value,
                })
              }
              placeholder="z. B. Rose am Unterarm"
            />
</label>

            {appointmentData.consultation_image_url && (
              <div className="appointment-reference-images">
                <h4>Referenzbilder</h4>

                <div className="appointment-reference-image-grid">
                  {appointmentData.consultation_image_url
                    .split(",")
                    .map((url) => url.trim())
                    .filter(Boolean)
                    .map((url, index, images) => (
                      <img
                        key={`${url}-${index}`}
                        src={url}
                        alt=""
                        onClick={() =>
                          onOpenImages(images, index)
                        }
                      />
                    ))}
                </div>
              </div>
            )}

            <label className="appointment-field"><span>Tattoo-Stil · optional</span>
<select
              value={appointmentData.tattoo_category}
              onChange={(event) =>
                setAppointmentData({
                  ...appointmentData,
                  tattoo_category: event.target.value,
                })
              }
            >
              <option value="">Kategorie wählen</option>

              {categories.map((category) => (
                <option
                  key={category}
                  value={category}
                >
                  {category}
                </option>
              ))}
            </select>
</label>
          </div>

          <div className="form-section">
            <h4>Kosten & Dauer</h4>

            <label className="appointment-field"><span>Preis ab (€)</span>
<input
              type="text"
              inputMode="decimal"
              value={appointmentData.minRange}
              onChange={(event) =>
                setAppointmentData({
                  ...appointmentData,
                  minRange: event.target.value,
                })
              }
              placeholder="Mindestpreis (€)"
              readOnly={isRetouch}
              aria-label="Mindestpreis (€)"
            />
</label>

            <label className="appointment-field"><span>Preis bis (€)</span>
<input
              type="text"
              inputMode="decimal"
              value={appointmentData.maxRange}
              onChange={(event) =>
                setAppointmentData({
                  ...appointmentData,
                  maxRange: event.target.value,
                })
              }
              placeholder="Höchstpreis (€)"
              readOnly={isRetouch}
              aria-label="Höchstpreis (€)"
            />
</label>

            <label className="appointment-field"><span>Anzahlung (€)</span>
<input
              type="text"
              inputMode="decimal"
              value={appointmentData.deposit ?? ""}
              onChange={(event) =>
                setAppointmentData({
                  ...appointmentData,
                  deposit: event.target.value,
                })
              }
              placeholder="Anzahlung (€)"
              readOnly={isRetouch}
              aria-label="Anzahlung (€)"
            />
</label>

            {isRetouch && (
              <div className="retouch-duration-switch" role="group" aria-label="Dauer">
                {[30, 60].map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    aria-pressed={Number(appointmentData.duration) === minutes}
                    onClick={() => setAppointmentData((previousData) => ({ ...previousData, duration: minutes }))}
                  >{minutes} Min.</button>
                ))}
              </div>
            )}

            <label className="appointment-field"><span>Dauer (Minuten)</span>
<input
              type="number"
              value={appointmentData.duration}
              min="1"
              aria-label="Dauer (Minuten)"
              onChange={(event) =>
                setAppointmentData({
                  ...appointmentData,
                  duration: event.target.value,
                })
              }
              placeholder="Dauer (Minuten)"
              required
            />
</label>
          </div>
        </div>

        </div>
        <div className="new-appointment-actions">
        <button
          type="submit"
          className="add-appointment-submit"
          disabled={!appointmentData.dayId || appointmentData.isSaving}
        >
          {appointmentData.isSaving ? 'Wird gespeichert…' : isRetouch ? 'Nachstechtermin speichern' : 'Termin hinzufügen'}
        </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddAppointmentModal;
